import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import EmailIdentityEditor from '@/features/team/EmailIdentityEditor'
import { useMe, useUpdateMe, useUploadMyPhoto } from '@/features/team/useTeam'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import type { User } from '@/api/users'

/**
 * "Edit my signature" from right where the email is being written — the same editor as My profile,
 * without leaving the deal. Saving refreshes the open preview so the new signature shows at once.
 */
export default function MySignatureModal({ onClose }: { onClose: () => void }) {
  const { data: me } = useMe()
  return (
    <Modal title="My email signature" onClose={onClose} size="lg">
      {me ? <Form me={me} onClose={onClose} /> : <p className="text-sm text-gray-400">Loading…</p>}
    </Modal>
  )
}

function Form({ me, onClose }: { me: User; onClose: () => void }) {
  const qc = useQueryClient()
  const update = useUpdateMe()
  const upload = useUploadMyPhoto()
  const [photoUrl, setPhotoUrl] = useState(me.photoUrl ?? '')
  const [signature, setSignature] = useState(me.emailSignature ?? '')
  const [error, setError] = useState<string | null>(null)

  function save() {
    setError(null)
    update.mutate(
      { fullName: me.fullName, emailSignature: signature.trim(), photoUrl: photoUrl.trim() },
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: ['message-preview'] })
          onClose()
        },
        onError: (err) => setError(apiErrorMessage(err)),
      },
    )
  }

  return (
    <div className="space-y-4">
      <EmailIdentityEditor
        photoUrl={photoUrl}
        onPhotoUrlChange={setPhotoUrl}
        signature={signature}
        onSignatureChange={setSignature}
        onUpload={(file) =>
          upload.mutate(file, { onSuccess: (u) => setPhotoUrl(u.photoUrl ?? ''), onError: (err) => setError(apiErrorMessage(err)) })
        }
        uploading={upload.isPending}
      />
      {error && <p className="text-danger text-xs">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className={modalButton.ghost}>
          Cancel
        </button>
        <button type="button" onClick={save} disabled={update.isPending} className={modalButton.primary}>
          {update.isPending ? 'Saving…' : 'Save signature'}
        </button>
      </div>
    </div>
  )
}
